<template>
  <SearchBuilder
    :id="id"
    v-model:filters="filters"
    :possible-filter-options="possibleFilterOptions"
    :aria-label="
      t('search.paramSearchCriteria', { param: t('shipments.shipments') })
    "
    class="shipment-search-builder"
  >
    <template #filters>
      <template v-for="filter in filters" :key="filter">
        <component
          :is="availableFiltersMap.get(filter)"
          :id="`criteria-${filter}`"
          v-model="criteria"
          :ref-number-type="referenceNumbersMap.get(filter)"
        />
      </template>
    </template>
  </SearchBuilder>
</template>

<script lang="ts" setup>
import type { Component, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue } from '@rygen/rygen-ui/composables';
import { mapEnumToTranslatedDropdownValues } from '@rygen/rygen-ui/i18n';
import {
  ClientReferenceNumberType,
  DropdownOption,
  ReferenceNumberTypeLevel,
} from '@rygen/rygen-ui/model';
import { distinct, notNull } from '@rygen/rygen-ui/util';

import {
  useElasticsearch,
  useLocationGrouping,
  useReferenceNumberTypes,
} from '@/composables';
import { ShipmentSearchCriteria, ShipmentSearchFilter } from '@/model';
import { useClientStore } from '@/stores/Client';
import { useCurrentUserStore } from '@/stores/CurrentUser';

import AssignedUsersShipmentFilter from '@/components/search/AssignedUsersShipmentFilter.vue';
import BillingTermsFilter from '@/components/search/BillingTermsFilter.vue';
import CostEstimateFilter from '@/components/search/CostEstimateFilter.vue';
import DestinationLocationGroupsSearchFilter from '@/components/search/DestinationLocationGroupsSearchFilter.vue';
import DirectionFilter from '@/components/search/DirectionFilter.vue';
import EquipmentTypeFilter from '@/components/search/EquipmentTypeFilter.vue';
import FreightAllowanceFilter from '@/components/search/FreightAllowanceFilter.vue';
import IdFilter from '@/components/search/IdFilter.vue';
import OriginLocationGroupsSearchFilter from '@/components/search/OriginLocationGroupsSearchFilter.vue';
import ReferenceNumberFilter from '@/components/search/ReferenceNumberFilter.vue';
import RequestedModeFilter from '@/components/search/RequestedModeFilter.vue';
import SearchBuilder from '@/components/search/SearchBuilder.vue';
import SourceFilter from '@/components/search/SourceFilter.vue';
import TagFilter from '@/components/search/TagFilter.vue';
import ShipmentActualDeliveryArrivalFilter from '@/components/shipment/filter/ShipmentActualDeliveryArrivalFilter.vue';
import ShipmentActualDeliveryDepartureFilter from '@/components/shipment/filter/ShipmentActualDeliveryDepartureFilter.vue';
import ShipmentActualPickupArrivalFilter from '@/components/shipment/filter/ShipmentActualPickupArrivalFilter.vue';
import ShipmentActualPickupDepartureFilter from '@/components/shipment/filter/ShipmentActualPickupDepartureFilter.vue';
import ShipmentCancellationReasonFilter from '@/components/shipment/filter/ShipmentCancellationReasonFilter.vue';
import ShipmentClientFilter from '@/components/shipment/filter/ShipmentClientFilter.vue';
import ShipmentCreatedDateFilter from '@/components/shipment/filter/ShipmentCreatedDateFilter.vue';
import ShipmentCustomerRoutedFilter from '@/components/shipment/filter/ShipmentCustomerRoutedFilter.vue';
import ShipmentDeliveryAppointmentRequiredFilter from '@/components/shipment/filter/ShipmentDeliveryAppointmentRequiredFilter.vue';
import ShipmentDestinationFilter from '@/components/shipment/filter/ShipmentDestinationFilter.vue';
import ShipmentExpectedDeliveryFilter from '@/components/shipment/filter/ShipmentExpectedDeliveryFilter.vue';
import ShipmentExpectedPickupFilter from '@/components/shipment/filter/ShipmentExpectedPickupFilter.vue';
import ShipmentFinancialStatusFilter from '@/components/shipment/filter/ShipmentFinancialStatusFilter.vue';
import FreightAllowanceEnabledFilter from '@/components/shipment/filter/ShipmentFreightAllowanceEnabledFilter.vue';
import ShipmentGLCodeFilter from '@/components/shipment/filter/ShipmentGLCodeFilter.vue';
import ShipmentHandlingUnitFilter from '@/components/shipment/filter/ShipmentHandlingUnitFilter.vue';
import ShipmentHazmatFilter from '@/components/shipment/filter/ShipmentHazmatFilter.vue';
import ShipmentHealthFilter from '@/components/shipment/filter/ShipmentHealthFilter.vue';
import ShipmentHotFilter from '@/components/shipment/filter/ShipmentHotFilter.vue';
import ShipmentManifestNumberFilter from '@/components/shipment/filter/ShipmentManifestNumberFilter.vue';
import ShipmentManifestStatusFilter from '@/components/shipment/filter/ShipmentManifestStatusFilter.vue';
import ShipmentModeFilter from '@/components/shipment/filter/ShipmentModeFilter.vue';
import ShipmentMultistopFilter from '@/components/shipment/filter/ShipmentMultistopFilter.vue';
import ShipmentOriginFilter from '@/components/shipment/filter/ShipmentOriginFilter.vue';
import ShipmentParcelPickupFilter from '@/components/shipment/filter/ShipmentParcelPickupFilter.vue';
import ShipmentPayableFilter from '@/components/shipment/filter/ShipmentPayableFilter.vue';
import ShipmentPickupAppointmentRequiredFilter from '@/components/shipment/filter/ShipmentPickupAppointmentRequiredFilter.vue';
import ShipmentProductNumberFilter from '@/components/shipment/filter/ShipmentProductNumberFilter.vue';
import ShipmentRequestedDeliveryFilter from '@/components/shipment/filter/ShipmentRequestedDeliveryFilter.vue';
import ShipmentRequestedPickupFilter from '@/components/shipment/filter/ShipmentRequestedPickupFilter.vue';
import ShipmentScacFilter from '@/components/shipment/filter/ShipmentScacFilter.vue';
import ShipmentScheduledDeliveryFilter from '@/components/shipment/filter/ShipmentScheduledDeliveryFilter.vue';
import ShipmentScheduledPickupFilter from '@/components/shipment/filter/ShipmentScheduledPickupFilter.vue';
import ShipmentServiceFilter from '@/components/shipment/filter/ShipmentServiceFilter.vue';
import ShipmentStatusFilter from '@/components/shipment/filter/ShipmentStatusFilter.vue';
import ShipmentSwcFilter from '@/components/shipment/filter/ShipmentSwcFilter.vue';
import ShipmentWeightFilter from '@/components/shipment/filter/ShipmentWeightFilter.vue';

const SHIPMENT_FILTERS: Record<ShipmentSearchFilter, Component> = {
  [ShipmentSearchFilter.CLIENT]: ShipmentClientFilter,
  [ShipmentSearchFilter.ID]: IdFilter,
  [ShipmentSearchFilter.SCAC]: ShipmentScacFilter,
  [ShipmentSearchFilter.MANIFEST_NUMBER]: ShipmentManifestNumberFilter,
  [ShipmentSearchFilter.MANIFEST_STATUS]: ShipmentManifestStatusFilter,
  [ShipmentSearchFilter.SERVICE]: ShipmentServiceFilter,
  [ShipmentSearchFilter.MODE]: ShipmentModeFilter,
  [ShipmentSearchFilter.HEALTH]: ShipmentHealthFilter,
  [ShipmentSearchFilter.STATUS]: ShipmentStatusFilter,
  [ShipmentSearchFilter.FINANCIAL_STATUS]: ShipmentFinancialStatusFilter,
  [ShipmentSearchFilter.ORIGIN]: ShipmentOriginFilter,
  [ShipmentSearchFilter.DESTINATION]: ShipmentDestinationFilter,
  [ShipmentSearchFilter.ORIGIN_LOCATION_GROUPS]:
    OriginLocationGroupsSearchFilter,
  [ShipmentSearchFilter.DESTINATION_LOCATION_GROUPS]:
    DestinationLocationGroupsSearchFilter,
  [ShipmentSearchFilter.EXPECTED_PICKUP]: ShipmentExpectedPickupFilter,
  [ShipmentSearchFilter.ACTUAL_PICKUP_ARRIVAL]:
    ShipmentActualPickupArrivalFilter,
  [ShipmentSearchFilter.ACTUAL_PICKUP_DEPARTURE]:
    ShipmentActualPickupDepartureFilter,
  [ShipmentSearchFilter.REQUESTED_PICKUP]: ShipmentRequestedPickupFilter,
  [ShipmentSearchFilter.SCHEDULED_PICKUP]: ShipmentScheduledPickupFilter,
  [ShipmentSearchFilter.PICKUP_APPOINTMENT_REQUIRED]:
    ShipmentPickupAppointmentRequiredFilter,
  [ShipmentSearchFilter.EXPECTED_DELIVERY]: ShipmentExpectedDeliveryFilter,
  [ShipmentSearchFilter.ACTUAL_DELIVERY_ARRIVAL]:
    ShipmentActualDeliveryArrivalFilter,
  [ShipmentSearchFilter.ACTUAL_DELIVERY_DEPARTURE]:
    ShipmentActualDeliveryDepartureFilter,
  [ShipmentSearchFilter.REQUESTED_DELIVERY]: ShipmentRequestedDeliveryFilter,
  [ShipmentSearchFilter.SCHEDULED_DELIVERY]: ShipmentScheduledDeliveryFilter,
  [ShipmentSearchFilter.DELIVERY_APPOINTMENT_REQUIRED]:
    ShipmentDeliveryAppointmentRequiredFilter,
  [ShipmentSearchFilter.CREATED_DATE]: ShipmentCreatedDateFilter,
  [ShipmentSearchFilter.GL_CODE]: ShipmentGLCodeFilter,
  [ShipmentSearchFilter.TAG]: TagFilter,
  [ShipmentSearchFilter.MULTISTOP]: ShipmentMultistopFilter,
  [ShipmentSearchFilter.PRODUCT_NUMBER]: ShipmentProductNumberFilter,
  [ShipmentSearchFilter.HAS_PARCEL_PICKUP_APPOINTMENT]:
    ShipmentParcelPickupFilter,
  [ShipmentSearchFilter.HOT]: ShipmentHotFilter,
  [ShipmentSearchFilter.HAZMAT]: ShipmentHazmatFilter,
  [ShipmentSearchFilter.PAYABLE]: ShipmentPayableFilter,
  [ShipmentSearchFilter.DIRECTION]: DirectionFilter,
  [ShipmentSearchFilter.BILLING_TERMS]: BillingTermsFilter,
  [ShipmentSearchFilter.EQUIPMENT_TYPE]: EquipmentTypeFilter,
  [ShipmentSearchFilter.FREIGHT_ALLOWANCE_ENABLED]:
    FreightAllowanceEnabledFilter,
  [ShipmentSearchFilter.FREIGHT_ALLOWANCE]: FreightAllowanceFilter,
  [ShipmentSearchFilter.COST_ESTIMATE]: CostEstimateFilter,
  [ShipmentSearchFilter.ASSIGNED_USERS]: AssignedUsersShipmentFilter,
  [ShipmentSearchFilter.CANCELLATION_REASON]: ShipmentCancellationReasonFilter,
  [ShipmentSearchFilter.REQUESTED_MODE]: RequestedModeFilter,
  [ShipmentSearchFilter.WEIGHT]: ShipmentWeightFilter,
  [ShipmentSearchFilter.HANDLING_UNIT]: ShipmentHandlingUnitFilter,
  [ShipmentSearchFilter.SOURCE]: SourceFilter,
  [ShipmentSearchFilter.SWC]: ShipmentSwcFilter,
  [ShipmentSearchFilter.CUSTOMER_ROUTED]: ShipmentCustomerRoutedFilter,
};

const ELASTICSEARCH_ONLY_FILTERS = [
  ShipmentSearchFilter.FREIGHT_ALLOWANCE,
  ShipmentSearchFilter.COST_ESTIMATE,
  ShipmentSearchFilter.WEIGHT,
  ShipmentSearchFilter.HANDLING_UNIT,
  ShipmentSearchFilter.MANIFEST_NUMBER,
  ShipmentSearchFilter.MANIFEST_STATUS,
];

const LOCATION_GROUPING_ONLY_FILTERS = [
  ShipmentSearchFilter.ORIGIN_LOCATION_GROUPS,
  ShipmentSearchFilter.DESTINATION_LOCATION_GROUPS,
];

const DEFAULT_VISIBLE_FILTERS = [
  ShipmentSearchFilter.CLIENT,
  ShipmentSearchFilter.ID,
  ShipmentSearchFilter.ORIGIN,
  ShipmentSearchFilter.DESTINATION,
  ShipmentSearchFilter.TAG,
];

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: ShipmentSearchCriteria,
    default: () => new ShipmentSearchCriteria(),
  },
});
const { id, modelValue } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', modelValue: ShipmentSearchCriteria): void;
}>();

const { hasDomainOptions, loadClients } = useClientStore();
const { selectedDomainId } = useCurrentUserStore();
const { t } = useI18n();
const { internalValue: criteria } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<ShipmentSearchCriteria>) =>
    new ShipmentSearchCriteria(opts),
});
const {
  lineItemReferenceNumberTypes,
  loadReferenceNumberTypes,
  orderReferenceNumberTypes,
  shipmentReferenceNumberTypes,
} = useReferenceNumberTypes();
const { isElasticsearchEnabled } = useElasticsearch({ searchType: 'shipment' });
const { isLocationGroupingEnabled } = useLocationGrouping();

const shipmentSearchFilters = mapEnumToTranslatedDropdownValues(
  ShipmentSearchFilter,
  'ShipmentSearchFilter',
);

const filters: Ref<Set<ShipmentSearchFilter | string>> = ref(
  new Set(DEFAULT_VISIBLE_FILTERS),
);

const defaultFilters = computed(() => {
  const filterSet = new Set<ShipmentSearchFilter | string>(
    DEFAULT_VISIBLE_FILTERS,
  );
  if (!hasDomainOptions.value) {
    filterSet.delete(ShipmentSearchFilter.CLIENT);
  }
  return filterSet;
});

const referenceNumbers: Ref<ClientReferenceNumberType[]> = computed(() => {
  const shipmentTypes = shipmentReferenceNumberTypes.value.data ?? [];
  const orderTypes = orderReferenceNumberTypes.value.data ?? [];
  const lineItemTypes = lineItemReferenceNumberTypes.value.data ?? [];

  return [...shipmentTypes, ...orderTypes, ...lineItemTypes].filter(distinct());
});

const referenceNumberNaturalIds: Ref<Set<string>> = computed(() => {
  const naturalIds = referenceNumbers.value
    .map((r) => r.referenceNumberType?.naturalId)
    .filter(notNull);
  return new Set(naturalIds);
});

const referenceNumberFilters = computed((): DropdownOption<string>[] => {
  return [...referenceNumberNaturalIds.value]
    .map((naturalId) => {
      const refNumType = referenceNumbersMap.value.get(naturalId);
      if (!refNumType) {
        return;
      }

      return {
        value: naturalId,
        label: refNumType.referenceNumberType?.name ?? '-',
      };
    })
    .filter(notNull);
});

const possibleFilterOptions = computed(() => {
  return shipmentSearchFilters
    .filter(
      (opt) =>
        opt.value !== ShipmentSearchFilter.CLIENT || hasDomainOptions.value,
    )
    .filter(
      (opt) =>
        !ELASTICSEARCH_ONLY_FILTERS.includes(
          opt.value as ShipmentSearchFilter,
        ) || isElasticsearchEnabled.value,
    )
    .filter(
      (opt) =>
        !LOCATION_GROUPING_ONLY_FILTERS.includes(
          opt.value as ShipmentSearchFilter,
        ) || isLocationGroupingEnabled.value,
    )
    .concat(referenceNumberFilters.value);
});

const availableFiltersMap = computed(() => {
  const basicFilters = Object.entries(SHIPMENT_FILTERS).filter(
    ([filterType, _component]) => {
      const shipmentSearchFilter = filterType as ShipmentSearchFilter;
      return (
        (!ELASTICSEARCH_ONLY_FILTERS.includes(shipmentSearchFilter) ||
          isElasticsearchEnabled.value) &&
        (!LOCATION_GROUPING_ONLY_FILTERS.includes(shipmentSearchFilter) ||
          isLocationGroupingEnabled.value)
      );
    },
  );
  const refNumFilters: [string, Component][] = [
    ...referenceNumberNaturalIds.value,
  ].map((naturalId) => [naturalId, ReferenceNumberFilter]);
  const allFilters = [...basicFilters, ...refNumFilters];
  return new Map<string, Component>(allFilters);
});

const referenceNumbersMap: Ref<Map<string, ClientReferenceNumberType>> =
  computed(() => {
    const entries = referenceNumbers.value
      .map((refNum) => {
        const naturalId = refNum.referenceNumberType?.naturalId;
        if (naturalId) {
          return [naturalId, refNum] as [string, ClientReferenceNumberType];
        }
      })
      .filter(notNull);
    return new Map(entries);
  });

watch(
  criteria,
  (newVal: ShipmentSearchCriteria) => {
    if (newVal.ownerNaturalIds.length) {
      filters.value.add(ShipmentSearchFilter.CLIENT);
    }
    if (newVal.shipmentId.length) {
      filters.value.add(ShipmentSearchFilter.ID);
    }
    if (newVal.scac) {
      filters.value.add(ShipmentSearchFilter.SCAC);
    }
    if (newVal.manifestNumber) {
      filters.value.add(ShipmentSearchFilter.MANIFEST_NUMBER);
    }
    if (newVal.manifestStatuses.length) {
      filters.value.add(ShipmentSearchFilter.MANIFEST_STATUS);
    }
    if (newVal.services.length) {
      filters.value.add(ShipmentSearchFilter.SERVICE);
    }
    if (newVal.modes.length) {
      filters.value.add(ShipmentSearchFilter.MODE);
    }
    if (newVal.healths.length) {
      filters.value.add(ShipmentSearchFilter.HEALTH);
    }
    if (newVal.statuses.length) {
      filters.value.add(ShipmentSearchFilter.STATUS);
    }
    if (newVal.financialStatuses.length) {
      filters.value.add(ShipmentSearchFilter.FINANCIAL_STATUS);
    }
    if (
      newVal.originLocationId ||
      newVal.originLocationCode ||
      newVal.originCity ||
      newVal.originState ||
      newVal.originPostalCode ||
      newVal.originCountry
    ) {
      filters.value.add(ShipmentSearchFilter.ORIGIN);
    }
    if (
      newVal.destinationLocationId ||
      newVal.destinationLocationCode ||
      newVal.destinationCity ||
      newVal.destinationState ||
      newVal.destinationPostalCode ||
      newVal.destinationCountry
    ) {
      filters.value.add(ShipmentSearchFilter.DESTINATION);
    }
    if (isLocationGroupingEnabled.value) {
      if (newVal.originLocationGroupIds.length) {
        filters.value.add(ShipmentSearchFilter.ORIGIN_LOCATION_GROUPS);
      }
      if (newVal.destinationLocationGroupIds.length) {
        filters.value.add(ShipmentSearchFilter.DESTINATION_LOCATION_GROUPS);
      }
    }
    if (
      newVal.expectedPickupDateRange?.start ||
      newVal.expectedPickupDateRange?.end
    ) {
      filters.value.add(ShipmentSearchFilter.EXPECTED_PICKUP);
    }
    if (
      newVal.actualPickupArrivalDateRange?.start ||
      newVal.actualPickupArrivalDateRange?.end
    ) {
      filters.value.add(ShipmentSearchFilter.ACTUAL_PICKUP_ARRIVAL);
    }
    if (
      newVal.actualPickupDepartureDateRange?.start ||
      newVal.actualPickupDepartureDateRange?.end
    ) {
      filters.value.add(ShipmentSearchFilter.ACTUAL_PICKUP_DEPARTURE);
    }
    if (
      newVal.requestedPickupDateRange?.start ||
      newVal.requestedPickupDateRange?.end
    ) {
      filters.value.add(ShipmentSearchFilter.REQUESTED_PICKUP);
    }
    if (
      newVal.scheduledPickupDateRange?.start ||
      newVal.scheduledPickupDateRange?.end
    ) {
      filters.value.add(ShipmentSearchFilter.SCHEDULED_PICKUP);
    }
    if (newVal.pickupAppointmentRequired != null) {
      filters.value.add(ShipmentSearchFilter.PICKUP_APPOINTMENT_REQUIRED);
    }
    if (
      newVal.expectedDeliveryDateRange?.start ||
      newVal.expectedDeliveryDateRange?.end
    ) {
      filters.value.add(ShipmentSearchFilter.EXPECTED_DELIVERY);
    }
    if (
      newVal.actualDeliveryArrivalDateRange?.start ||
      newVal.actualDeliveryArrivalDateRange?.end
    ) {
      filters.value.add(ShipmentSearchFilter.ACTUAL_DELIVERY_ARRIVAL);
    }
    if (
      newVal.actualDeliveryDepartureDateRange?.start ||
      newVal.actualDeliveryDepartureDateRange?.end
    ) {
      filters.value.add(ShipmentSearchFilter.ACTUAL_DELIVERY_DEPARTURE);
    }
    if (
      newVal.requestedDeliveryDateRange?.start ||
      newVal.requestedDeliveryDateRange?.end
    ) {
      filters.value.add(ShipmentSearchFilter.REQUESTED_DELIVERY);
    }
    if (
      newVal.scheduledDeliveryDateRange?.start ||
      newVal.scheduledDeliveryDateRange?.end
    ) {
      filters.value.add(ShipmentSearchFilter.SCHEDULED_DELIVERY);
    }
    if (newVal.deliveryAppointmentRequired != null) {
      filters.value.add(ShipmentSearchFilter.DELIVERY_APPOINTMENT_REQUIRED);
    }
    if (newVal.createdDateRange?.start || newVal.createdDateRange?.end) {
      filters.value.add(ShipmentSearchFilter.CREATED_DATE);
    }
    if (newVal.referenceNumbers.length) {
      newVal.referenceNumbers.forEach((refNumber) => {
        if (refNumber.typeNaturalId !== undefined) {
          filters.value.add(refNumber.typeNaturalId);
        }
      });
    }
    if (newVal.glCodes.length) {
      filters.value.add(ShipmentSearchFilter.GL_CODE);
    }
    if (newVal.tags.length) {
      filters.value.add(ShipmentSearchFilter.TAG);
    }
    if (newVal.assignedUsers.length) {
      filters.value.add(ShipmentSearchFilter.ASSIGNED_USERS);
    }
    if (newVal.multistop != null) {
      filters.value.add(ShipmentSearchFilter.MULTISTOP);
    }
    if (newVal.productNumber) {
      filters.value.add(ShipmentSearchFilter.PRODUCT_NUMBER);
    }
    if (newVal.hot != null) {
      filters.value.add(ShipmentSearchFilter.HOT);
    }
    if (newVal.hazmat != null) {
      filters.value.add(ShipmentSearchFilter.HAZMAT);
    }
    if (newVal.payable != null) {
      filters.value.add(ShipmentSearchFilter.PAYABLE);
    }
    if (newVal.directions.length) {
      filters.value.add(ShipmentSearchFilter.DIRECTION);
    }
    if (newVal.billingTerms.length) {
      filters.value.add(ShipmentSearchFilter.BILLING_TERMS);
    }
    if (newVal.equipmentTypes.length) {
      filters.value.add(ShipmentSearchFilter.EQUIPMENT_TYPE);
    }
    if (newVal.freightAllowanceEnabled != null) {
      filters.value.add(ShipmentSearchFilter.FREIGHT_ALLOWANCE_ENABLED);
    }
    if (newVal.cancellationReason) {
      filters.value.add(ShipmentSearchFilter.CANCELLATION_REASON);
    }
    if (newVal.requestedMode.length) {
      filters.value.add(ShipmentSearchFilter.REQUESTED_MODE);
    }
    if (newVal.source) {
      filters.value.add(ShipmentSearchFilter.SOURCE);
    }
    if (newVal.hasParcelPickupAppointment != null) {
      filters.value.add(ShipmentSearchFilter.HAS_PARCEL_PICKUP_APPOINTMENT);
    }
    if (newVal.swc != null) {
      filters.value.add(ShipmentSearchFilter.SWC);
    }
    if (newVal.customerRouted != null) {
      filters.value.add(ShipmentSearchFilter.CUSTOMER_ROUTED);
    }
    if (isElasticsearchEnabled.value) {
      if (newVal.weight) {
        filters.value.add(ShipmentSearchFilter.WEIGHT);
      }
      if (newVal.handlingUnit) {
        filters.value.add(ShipmentSearchFilter.HANDLING_UNIT);
      }
      if (newVal.freightAllowance != null) {
        filters.value.add(ShipmentSearchFilter.FREIGHT_ALLOWANCE);
      }
      if (newVal.costEstimate != null) {
        filters.value.add(ShipmentSearchFilter.COST_ESTIMATE);
      }
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => criteria.value.savedSearchId,
  (newVal, oldVal) => {
    if (oldVal && !newVal) {
      filters.value = new Set(defaultFilters.value);
    }
  },
);

watch(
  filters,
  (
    newVal: Set<ShipmentSearchFilter | string>,
    oldVal: Set<ShipmentSearchFilter | string>,
  ) => {
    const removedFilters = [...oldVal].filter((f) => !newVal.has(f));
    removedFilters.forEach((filter) => {
      if (filter === ShipmentSearchFilter.CLIENT) {
        criteria.value.ownerNaturalIds = [];
      } else if (filter === ShipmentSearchFilter.ID) {
        criteria.value.shipmentId = [];
      } else if (filter === ShipmentSearchFilter.SCAC) {
        criteria.value.scac = undefined;
      } else if (filter === ShipmentSearchFilter.MANIFEST_NUMBER) {
        criteria.value.manifestNumber = undefined;
      } else if (filter === ShipmentSearchFilter.MANIFEST_STATUS) {
        criteria.value.manifestStatuses = [];
      } else if (filter === ShipmentSearchFilter.SERVICE) {
        criteria.value.services = [];
      } else if (filter === ShipmentSearchFilter.MODE) {
        criteria.value.modes = [];
      } else if (filter === ShipmentSearchFilter.HEALTH) {
        criteria.value.healths = [];
      } else if (filter === ShipmentSearchFilter.STATUS) {
        criteria.value.statuses = [];
      } else if (filter === ShipmentSearchFilter.FINANCIAL_STATUS) {
        criteria.value.financialStatuses = [];
      } else if (filter === ShipmentSearchFilter.ORIGIN) {
        criteria.value.originLocationId = undefined;
        criteria.value.originLocationCode = undefined;
        criteria.value.originCity = undefined;
        criteria.value.originState = undefined;
        criteria.value.originPostalCode = undefined;
        criteria.value.originCountry = undefined;
      } else if (filter === ShipmentSearchFilter.DESTINATION) {
        criteria.value.destinationLocationId = undefined;
        criteria.value.destinationLocationCode = undefined;
        criteria.value.destinationCity = undefined;
        criteria.value.destinationState = undefined;
        criteria.value.destinationPostalCode = undefined;
        criteria.value.destinationCountry = undefined;
      } else if (filter === ShipmentSearchFilter.ORIGIN_LOCATION_GROUPS) {
        criteria.value.originLocationGroupIds = [];
      } else if (filter === ShipmentSearchFilter.DESTINATION_LOCATION_GROUPS) {
        criteria.value.destinationLocationGroupIds = [];
      } else if (filter === ShipmentSearchFilter.EXPECTED_PICKUP) {
        criteria.value.expectedPickupDateRange = undefined;
      } else if (filter === ShipmentSearchFilter.ACTUAL_PICKUP_ARRIVAL) {
        criteria.value.actualPickupArrivalDateRange = undefined;
      } else if (filter === ShipmentSearchFilter.ACTUAL_PICKUP_DEPARTURE) {
        criteria.value.actualPickupDepartureDateRange = undefined;
      } else if (filter === ShipmentSearchFilter.REQUESTED_PICKUP) {
        criteria.value.requestedPickupDateRange = undefined;
      } else if (filter === ShipmentSearchFilter.SCHEDULED_PICKUP) {
        criteria.value.scheduledPickupDateRange = undefined;
      } else if (filter === ShipmentSearchFilter.PICKUP_APPOINTMENT_REQUIRED) {
        criteria.value.pickupAppointmentRequired = null;
      } else if (filter === ShipmentSearchFilter.EXPECTED_DELIVERY) {
        criteria.value.expectedDeliveryDateRange = undefined;
      } else if (filter === ShipmentSearchFilter.ACTUAL_DELIVERY_ARRIVAL) {
        criteria.value.actualDeliveryArrivalDateRange = undefined;
      } else if (filter === ShipmentSearchFilter.ACTUAL_DELIVERY_DEPARTURE) {
        criteria.value.actualDeliveryDepartureDateRange = undefined;
      } else if (filter === ShipmentSearchFilter.REQUESTED_DELIVERY) {
        criteria.value.requestedDeliveryDateRange = undefined;
      } else if (filter === ShipmentSearchFilter.SCHEDULED_DELIVERY) {
        criteria.value.scheduledDeliveryDateRange = undefined;
      } else if (
        filter === ShipmentSearchFilter.DELIVERY_APPOINTMENT_REQUIRED
      ) {
        criteria.value.deliveryAppointmentRequired = null;
      } else if (filter === ShipmentSearchFilter.CREATED_DATE) {
        criteria.value.createdDateRange = undefined;
      } else if (filter === ShipmentSearchFilter.GL_CODE) {
        criteria.value.glCodes = [];
      } else if (filter === ShipmentSearchFilter.TAG) {
        criteria.value.tags = [];
      } else if (filter === ShipmentSearchFilter.ASSIGNED_USERS) {
        criteria.value.assignedUsers = [];
      } else if (filter === ShipmentSearchFilter.MULTISTOP) {
        criteria.value.multistop = undefined;
      } else if (filter === ShipmentSearchFilter.PRODUCT_NUMBER) {
        criteria.value.productNumber = undefined;
      } else if (filter === ShipmentSearchFilter.HOT) {
        criteria.value.hot = null;
      } else if (filter === ShipmentSearchFilter.HAZMAT) {
        criteria.value.hazmat = null;
      } else if (filter === ShipmentSearchFilter.PAYABLE) {
        criteria.value.payable = null;
      } else if (filter === ShipmentSearchFilter.DIRECTION) {
        criteria.value.directions = [];
      } else if (filter === ShipmentSearchFilter.BILLING_TERMS) {
        criteria.value.billingTerms = [];
      } else if (filter === ShipmentSearchFilter.EQUIPMENT_TYPE) {
        criteria.value.equipmentTypes = [];
      } else if (filter === ShipmentSearchFilter.FREIGHT_ALLOWANCE_ENABLED) {
        criteria.value.freightAllowanceEnabled = null;
      } else if (filter === ShipmentSearchFilter.FREIGHT_ALLOWANCE) {
        criteria.value.freightAllowance = undefined;
      } else if (filter === ShipmentSearchFilter.COST_ESTIMATE) {
        criteria.value.costEstimate = undefined;
      } else if (filter === ShipmentSearchFilter.CANCELLATION_REASON) {
        criteria.value.cancellationReason = undefined;
      } else if (filter === ShipmentSearchFilter.REQUESTED_MODE) {
        criteria.value.requestedMode = [];
      } else if (filter === ShipmentSearchFilter.WEIGHT) {
        criteria.value.weight = undefined;
      } else if (filter === ShipmentSearchFilter.HANDLING_UNIT) {
        criteria.value.handlingUnit = undefined;
      } else if (filter === ShipmentSearchFilter.SOURCE) {
        criteria.value.source = undefined;
      } else if (
        filter === ShipmentSearchFilter.HAS_PARCEL_PICKUP_APPOINTMENT
      ) {
        criteria.value.hasParcelPickupAppointment = null;
      } else if (filter === ShipmentSearchFilter.SWC) {
        criteria.value.swc = null;
      } else if (filter === ShipmentSearchFilter.CUSTOMER_ROUTED) {
        criteria.value.customerRouted = null;
      }
    });
  },
);

watch(selectedDomainId, () => {
  // When domain changes, clear domain-specific filters
  criteria.value.ownerNaturalIds = [];
  criteria.value.shipmentId = [];
  criteria.value.originLocationId = undefined;
  criteria.value.originLocationCode = undefined;
  criteria.value.destinationLocationId = undefined;
  criteria.value.destinationLocationCode = undefined;
  criteria.value.savedSearchId = undefined;
  criteria.value.services = [];
  criteria.value.modes = [];
  criteria.value.referenceNumbers = [];
  criteria.value.glCodes = [];
  criteria.value.productNumber = undefined;
  criteria.value.tags = [];
  criteria.value.assignedUsers = [];
});

watch(
  selectedDomainId,
  () => {
    loadReferenceNumberTypes({
      level: ReferenceNumberTypeLevel.ORDER,
      bypassCache: true,
    });
    loadReferenceNumberTypes({
      level: ReferenceNumberTypeLevel.SHIPMENT,
      bypassCache: true,
    });
    loadReferenceNumberTypes({
      level: ReferenceNumberTypeLevel.LINE_ITEM,
      bypassCache: true,
    });
    loadClients({ bypassCache: true });
  },
  { immediate: true },
);

watch(
  hasDomainOptions,
  (newVal: boolean) => {
    if (newVal) {
      filters.value = new Set([ShipmentSearchFilter.CLIENT, ...filters.value]);
    } else {
      filters.value.delete(ShipmentSearchFilter.CLIENT);
    }
  },
  { immediate: true },
);
</script>
